/* Identity row editor — FULLY VISUAL forms (no JSON/CodeMirror). Users and
 * roles are edited as structured fields and every mutation runs a
 * config-plane workflow:
 *   add/edit user → wf_create_user / wf_update_user
 *   delete user   → wf_delete_user   (last-admin guard server-side)
 *   add/edit role → wf_create_role_admin / wf_update_role_admin
 *   delete role   → wf_delete_role   (structural + role-referenced guards,
 *                                     force-clean via cap_force_clean_role)
 * The id is engine-minted (u_/role_) by the caps — never author-typed.
 * Guards come back as closed OUTCOMES (validation_error · conflict ·
 * not_found), surfaced inline rather than as HTTP errors. */
import React, {
    useEffect,
    useState
} from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from 'shared/components/ui/dialog';
import {
    Loader2,
    Save,
    Trash2
} from 'lucide-react';
import {
    appEngineService,
    describeError,
    readWorkflow
} from '../../api/appEngine.service';
import type {
    WorkflowFailure
} from '../../api/appEngine.service';
import {
    useAppStudio
} from '../../AppStudioContext';
import {
    notify
} from 'shared/api/notifier';
import {
    DeleteNotices,
    RoleForm,
    UserForm
} from './IdentityForms';
import type {
    IdentityDoc
} from '../../api/types';

type Props = {
    collection: string; // 'users' | 'roles'
    doc: IdentityDoc | null; // null = create
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChanged: () => void;
    /** Role ids of the app, for the user Roles multi-select. */
    roleOptions: { value: string; label: string }[];
    /** Journey ids of the app, for the role Journeys multi-select. */
    journeyOptions: { value: string; label: string }[];
    /** Id of the ONLY user holding 'admin' (delete would brick role
     * management) — Delete is disabled client-side for that document; the
     * wf_delete_user composite also blocks it server-side. */
    soleAdminUserId?: string | null;
    /** When 'delete' (from a list trash button), open straight into the
     * delete confirm — reuses the guard / force-clean handling. */
    intent?: 'delete' | null;
    /** Optimistic removal: called with the id right after a confirmed delete so
     * the list drops it instantly (the slower re-fetch then reconciles). */
    onDeleted?: (id: string) => void;
    /** Optimistic upsert: called with the new/updated doc right after a
     * confirmed create/edit so the list shows it instantly (re-fetch
     * reconciles canonical fields like server-minted ids). */
    onUpserted?: (doc: IdentityDoc) => void;
};

/** Structural roles (locked contract) — undeletable, force or not. */
const STRUCTURAL_ROLES = new Set(['anonymous', 'admin']);

export default function IdentityEditDialog({
    collection,
    doc,
    open,
    onOpenChange,
    onChanged,
    roleOptions,
    journeyOptions,
    soleAdminUserId = null,
    intent = null,
    onDeleted,
    onUpserted
}: Props) {
    const {
        tenancy
    } = useAppStudio();
    const isUsers = collection === 'users';
    const creating = doc == null;

    // ── User form state ──────────────────────────────────────────────────
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [userRoles, setUserRoles] = useState<string[]>([]);
    // ── Role form state ──────────────────────────────────────────────────
    const [roleName, setRoleName] = useState('');
    const [roleJourneys, setRoleJourneys] = useState<string[]>([]);

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Role-referenced conflict (offer force-clean).
    const [conflict, setConflict] = useState<WorkflowFailure | null>(null);
    // Plain delete confirm (users + non-conflicting roles).
    const [confirmDelete, setConfirmDelete] = useState(false);

    const docId = doc?.id != null ? String(doc.id) : '';

    // Prefill on open. Password is ALWAYS blank on edit (blank = unchanged).
    useEffect(() => {
        if (!open) return;
        setError(null);
        setConflict(null);
        // Opened via a list trash button → jump straight to the delete confirm.
        setConfirmDelete(intent === 'delete' && !creating);
        setPassword('');
        if (isUsers) {
            setEmail(doc?.email != null ? String(doc.email) : '');
            setName(doc?.name != null ? String(doc.name) : '');
            setUserRoles(Array.isArray(doc?.roles) ? doc!.roles.map(String) : []);
        } else {
            setRoleName(doc?.name != null ? String(doc.name) : '');
            setRoleJourneys(
                Array.isArray(doc?.journeys) ? doc!.journeys.map(String) : []);
        }
    }, [open, doc, isUsers, intent, creating]);

    const structural = !isUsers && STRUCTURAL_ROLES.has(docId);
    const soleAdmin = isUsers && docId !== '' && docId === soleAdminUserId;
    const deleteBlocked = structural || soleAdmin;
    const deleteHint = structural
        ? "Structural role — 'anonymous' and 'admin' cannot be deleted"
        : soleAdmin
            ? 'Sole admin user — deleting it would leave nobody able to '
              + "manage roles; grant 'admin' to another user first"
            : undefined;

    const valid = isUsers
        ? email.trim().length > 0 && (creating ? password.length > 0 : true)
        : roleName.trim().length > 0;

    // Surface a closed workflow failure: role-referenced conflict opens the
    // force-clean affordance; everything else is an inline message.
    const surface = (failure: WorkflowFailure) => {
        if (!isUsers && failure.status === 'conflict') {
            setConflict(failure);
            setError(null);
        } else {
            setConflict(null);
            setError(failure.message);
        }
    };

    // Returns the workflow OUTPUT on success (truthy — `?? {}` so an empty
    // output still reads as success), or null on a closed failure / error.
    const run = async (wfId: string, input: any): Promise<any | null> => {
        setBusy(true);
        setError(null);
        try {
            const res = await appEngineService.executeWorkflowConfig(
                tenancy, wfId, input);
            const mapped = readWorkflow(res);
            if (mapped.ok) {
                return mapped.output ?? {};
            }
            surface(mapped.failure);
            return null;
        } catch (err) {
            setError(describeError(err));
            return null;
        } finally {
            setBusy(false);
        }
    };

    const saved = (label: string) => {
        notify(label, 'success');
        onChanged();
        onOpenChange(false);
    };

    const save = async () => {
        if (!valid || busy) return;
        if (isUsers) {
            if (creating) {
                const out = await run('wf_create_user', {
                    email: email.trim(),
                    name: name.trim() || undefined,
                    password,
                    roles: userRoles
                });
                if (out) {
                    // Optimistic: show the new row now (re-fetch reconciles).
                    onUpserted?.({ id: out.user_id, email: email.trim(),
                        name: name.trim(), roles: userRoles });
                    saved(`Created user ${email.trim()}`);
                }
            } else {
                // Blank password = keep the stored hash (the workflow's
                // when()/ABSENT drops password when not supplied).
                const input: Record<string, any> = {
                    user_id: docId,
                    name: name.trim() || undefined,
                    roles: userRoles
                };
                if (password.length > 0) input.password = password;
                const out = await run('wf_update_user', input);
                if (out) {
                    // email isn't changed by wf_update_user — keep the stored one.
                    onUpserted?.({ id: docId, email: doc?.email,
                        name: name.trim(), roles: userRoles });
                    saved(`Saved user ${email.trim() || docId}`);
                }
            }
        } else {
            if (creating) {
                const out = await run('wf_create_role_admin', {
                    name: roleName.trim(),
                    journeys: roleJourneys
                });
                if (out) {
                    onUpserted?.({ id: out.role_id, name: roleName.trim(),
                        journeys: roleJourneys });
                    saved(`Created role ${roleName.trim()}`);
                }
            } else {
                // wf_update_role_admin SETS the role name + the WHOLE
                // journeys list (no append).
                const out = await run('wf_update_role_admin', {
                    role_id: docId,
                    name: roleName.trim(),
                    journeys: roleJourneys
                });
                if (out) {
                    onUpserted?.({ id: docId, name: roleName.trim(),
                        journeys: roleJourneys });
                    saved(`Saved role ${roleName.trim() || docId}`);
                }
            }
        }
    };

    // Delete — users go through wf_delete_user (last-admin guard); roles go
    // through wf_delete_role (structural + referenced guards). force only
    // applies to roles (re-invoke with force:true after a conflict).
    const doDelete = async (force = false) => {
        if (isUsers) {
            const ok = await run('wf_delete_user', {
 user_id: docId
});
            if (ok) {
                onDeleted?.(docId);
                saved(`Deleted user ${doc?.email || docId}`);
            }
            return;
        }
        const ok = await run('wf_delete_role', {
 role_id: docId,
force
});
        if (ok) {
            onDeleted?.(docId);
            saved(force
                ? `Deleted role ${docId} and removed it from referencing users`
                : `Deleted role ${docId}`);
        }
    };

    const title = creating
        ? `New ${isUsers ? 'user' : 'role'}`
        : `${isUsers ? (doc?.email || docId) : (doc?.name || docId)}`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        {isUsers
                            ? 'A user holds one or more roles — roles decide which '
                              + 'journeys they can open.'
                            : 'A role is the access map — the journeys on it are that '
                              + "role's home menu (menu ≡ gate)."}
                    </DialogDescription>
                </DialogHeader>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14
                }}>
                    {isUsers ? (
                        <UserForm
                            creating={creating}
                            email={email}
                            name={name}
                            password={password}
                            roles={userRoles}
                            roleOptions={roleOptions}
                            onEmail={setEmail}
                            onName={setName}
                            onPassword={setPassword}
                            onRoles={setUserRoles} />
                    ) : (
                        <RoleForm
                            creating={creating}
                            structural={structural}
                            docId={docId}
                            name={roleName}
                            journeys={roleJourneys}
                            journeyOptions={journeyOptions}
                            onName={setRoleName}
                            onJourneys={setRoleJourneys} />
                    )}

                    {error && (
                        <div className="aps-errors">
                            <p className="aps-errors-title">Rejected</p>
                            <ul>
                                <li>{error}</li>
                            </ul>
                        </div>
                    )}

                    <DeleteNotices
                        isUsers={isUsers}
                        title={title}
                        busy={busy}
                        conflict={conflict}
                        confirmDelete={confirmDelete}
                        onCancelConfirm={() => setConfirmDelete(false)}
                        onKeepRole={() => setConflict(null)}
                        onDelete={() => doDelete(false)}
                        onForceDelete={() => doDelete(true)} />

                    <div style={{
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'flex-end'
                    }}>
                        {!creating && (
                            <button className="aps-btn aps-btn-ghost" style={{
                                color: deleteBlocked
                                    ? 'var(--aps-text-subtle)'
                                    : 'var(--aps-danger)',
                                marginRight: 'auto'
                            }} disabled={busy || deleteBlocked}
                            title={deleteHint}
                            onClick={() => {
                                setConflict(null);
                                setConfirmDelete(true);
                            }}>
                                <Trash2 size={15} />
                                Delete
                            </button>
                        )}
                        <button className="aps-btn aps-btn-ghost" disabled={busy}
                            onClick={() => onOpenChange(false)}>
                            Cancel
                        </button>
                        <button className="aps-btn aps-btn-primary"
                            disabled={busy || !valid} onClick={save}>
                            {busy
                                ? <Loader2 size={15} className="aps-spin" />
                                : <Save size={15} />}
                            {creating ? 'Create' : 'Save'}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
